import { createRouter, createWebHistory, Router } from 'vue-router';
import { getRoutes } from '@/router/routes';
import { useOperationStore } from '@/store/storeOperation';
import { useAuth } from '@/composables/useAuth';
import { isFeatureEnabled, type FeatureKey } from '@/router/featureFlags';

// Global router instance that can be accessed before initialization
let routerInstance: Router | null = null;

export function getRouter(): Router | null {
    return routerInstance;
}

export default async function createRouterInstance(): Promise<Router> {
    const routes = await getRoutes(); // Await the route generation

    const router: Router = createRouter({
        history: createWebHistory(),
        routes,
    });

    // Global navigation guard
    router.beforeEach(async (to, _from) => {
        const isDevelopment = import.meta.env.DEV;
        const accessToken = localStorage.getItem('access-token');
        const allowAnonymousAuth = to.meta.allowAnonymousAuth === true;

        // Auth check (skip in development)
        if (!isDevelopment && !allowAnonymousAuth && !accessToken) {
            return '/logout';
        }

        // Admin guard
        if (to.meta.requiresAdmin === true && !isDevelopment) {
            const { refresh, isAdmin } = useAuth();
            refresh();
            if (!isAdmin.value) return '/';
        }

        // Feature-flag guard. Every route belonging to a gated feature
        // carries `meta.feature = '<key>'` (see router/routes.ts). If the
        // corresponding VITE_FEATURE_<KEY>_ENABLED env var isn't set to
        // 'true' at build time, the feature is off — bounce the user to
        // '/' the same way the admin guard does. This catches direct URL
        // entry, browser back-navigation, and bookmarked deep links; menu
        // items for the same features are also hidden by featureDisabled
        // in homeRouteAndMenuStructure.ts.
        const featureKey = to.meta.feature as FeatureKey | undefined;
        if (featureKey && !isFeatureEnabled(featureKey)) {
            return '/';
        }

        // Operation guard: if navigating to a operation route, sync the store
        if (to.params.operationId) {
            const operationStore = useOperationStore();
            const operationId = to.params.operationId as string;

            // Fetch operations if not yet loaded
            if (operationStore.operations.length === 0) {
                await operationStore.fetchOperations();
            }

            // Validate the operationId
            const valid = operationStore.operations.some(w => w.id === operationId);
            if (!valid && operationStore.operations.length > 0) {
                // Redirect to the first valid operation
                return `/operation/${operationStore.operations[0].id}`;
            }

            operationStore.setCurrentOperation(operationId);

            // Permissions depend on the active operation — reload on operation switch.
            await useAuth().loadPermissions();
        }

        return true;
    });

    // Store the router instance globally
    routerInstance = router;

    return router;
}
